import * as React from 'react';
import { useDebounce } from '../../../hooks/debounce';
import { useOutsideEvent } from '../../../hooks/outside-event';
import { BaSeTheme } from '../../../theme';
import { ThemeNamedColorOrColorValue } from '../../../theme/theme-interface';
import { BaSeIcon } from '../../image/icon';
import { BaSeParagraph } from '../../typography/paragraph/paragraph';
import { BaSeSmall1 } from '../../typography/small/small1';
import { BaSeInput, InputProps } from '../text/input';
import {
  AutocompleteResultInline,
  AutocompleteResultItemImage,
  AutocompleteResultList,
  AutocompleteResultListItem,
  AutocompleteWrapper,
  AutocompleteWrapperProps,
  HideTopOfAutocompleteResultList,
} from './autocomplete-styled';

export type AutocompleteResultItemId = number | string;

export interface AutocompleteResultItem {
  id: AutocompleteResultItemId;
  label: string;
  description?: string;
  iconName?: string;
  imageSource?: string;
  status?: string;
  statusColor?: ThemeNamedColorOrColorValue;
}

export type AutocompleteResult =
  | AutocompleteResultItem[]
  | Promise<AutocompleteResultItem[]>;

export interface AutoCompleteExtraAction {
  label: string;
  iconName?: string;
  showOnlyResultIsEmpty?: boolean;
  onClick: (value: string, close: () => void) => void;
}

export interface AutoCompleteExtraItem {
  text: string;
  iconName?: string;
  showOnlyResultIsEmpty?: boolean;
  color?: string;
  isBold?: boolean;
}

export interface AutocompleteProps
  extends Pick<AutocompleteWrapperProps, 'mode'>,
    Pick<
      InputProps,
      | 'inputMode'
      | 'isDisabled'
      | 'label'
      | 'placeholder'
      | 'showHelpButton'
      | 'complement'
      | 'moreInfoLabel'
      | 'moreInfoDetails'
      | 'subLabel'
      | 'helpButtonProps'
      | 'width'
      | 'size'
      | 'id'
      | 'name'
      | 'dataset'
    > {
  minLengthToSearch?: number;
  imageAndIconSize?: number;
  extraActions?: AutoCompleteExtraAction[];
  extraItems?: AutoCompleteExtraItem[];
  onSearchTerm(term: string): AutocompleteResult;
  onSelectedItem?(item: AutocompleteResultItem, clear: () => void): void;
  onClearSelection?(): void;
}

const ExtraItem = ({
  extraItem: {
    text,
    iconName,
    showOnlyResultIsEmpty,
    color = BaSeTheme.colors.defaultColors.black,
    isBold = true,
  },
  isEmpty,
}: {
  extraItem: AutoCompleteExtraItem;
  isEmpty?: boolean;
}) => {
  return !showOnlyResultIsEmpty || (showOnlyResultIsEmpty && isEmpty) ? (
    <AutocompleteResultListItem
      className={`BaSe--autocomplete-result-list-item `}
      cursor="auto"
    >
      {iconName && (
        <BaSeIcon
          name={iconName}
          size={20}
          description={`Ícone do texto final: ${text}`}
          color={color}
        />
      )}
      <BaSeParagraph isBold={isBold} color={color}>
        {text}
      </BaSeParagraph>
    </AutocompleteResultListItem>
  ) : null;
};

const ExtraAction = ({
  extraAction: { label, onClick, iconName, showOnlyResultIsEmpty },
  isEmpty,
  term,
  setIsOpen,
}: {
  extraAction: AutoCompleteExtraAction;
  isEmpty?: boolean;
  term: string;
  setIsOpen: (bool: boolean) => void;
}) => {
  const [isActive, setIsActive] = React.useState(false);
  const textColor = React.useMemo(
    () =>
      isActive
        ? BaSeTheme.colors.defaultColors.white
        : BaSeTheme.colors.defaultColors.black,
    [isActive],
  );
  const closeCallback = React.useCallback(() => setIsOpen(false), []);

  return !showOnlyResultIsEmpty || (showOnlyResultIsEmpty && isEmpty) ? (
    <AutocompleteResultListItem
      role="button"
      tabIndex={0}
      className={`BaSe--autocomplete-result-list-item ${
        isActive ? 'active' : ''
      }`}
      onFocus={() => setIsActive(true)}
      onBlur={() => setIsActive(false)}
      onClick={() => onClick(term, closeCallback)}
      onKeyDown={(e) => e.key === 'Enter' && onClick(term, closeCallback)}
    >
      {iconName && (
        <BaSeIcon
          name={iconName}
          size={20}
          description={`Ícone da ação: ${label}`}
          color={textColor}
        />
      )}
      <BaSeParagraph color={textColor}>{label}</BaSeParagraph>
    </AutocompleteResultListItem>
  ) : null;
};

export const BaSeAutocomplete: React.FC<AutocompleteProps> = ({
  id,
  name,
  dataset,
  label = '',
  mode = 'list',
  placeholder = '',
  width = '100%',
  inputMode = 'search',
  isDisabled = false,
  minLengthToSearch = 3,
  showHelpButton = false,
  complement = '',
  moreInfoLabel = '',
  moreInfoDetails = '',
  size = 'medium',
  subLabel = '',
  helpButtonProps = {},
  imageAndIconSize = 48,
  onSearchTerm,
  onSelectedItem,
  onClearSelection,
  extraActions = [],
  extraItems = [],
}) => {
  const [selectedIndex, setSelectedIndex] = React.useState<number>(0);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isInputFocused, setIsInputFocused] = React.useState<boolean>(false);
  const [hasError, setHasError] = React.useState<boolean>(false);
  const [value, setValue] = React.useState<string>('');
  const [resultList, setResultList] = React.useState<AutocompleteResultItem[]>(
    [],
  );

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  const term = useDebounce<string>(value, 350);
  useOutsideEvent(wrapperRef, setIsOpen);

  const hasAlwaysOpenedExtraItem = React.useMemo(
    () =>
      extraItems.some(({ showOnlyResultIsEmpty }) => !showOnlyResultIsEmpty),
    [extraItems],
  );

  const foundResult = React.useMemo(
    () =>
      !hasError &&
      term?.length >= minLengthToSearch &&
      (hasAlwaysOpenedExtraItem ||
        resultList?.length > 0 ||
        extraActions.length > 0),
    [
      hasError,
      resultList,
      extraActions,
      term,
      minLengthToSearch,
      hasAlwaysOpenedExtraItem,
    ],
  );

  const getResult = React.useCallback(
    () => resultList?.[selectedIndex]?.label,
    [resultList, selectedIndex],
  );

  const resultInline = React.useCallback(() => {
    const regExp = new RegExp(`(^.{${term.length}})(.*$)`);
    return getResult()?.replace?.(regExp, `<span>${term}</span>$2`);
  }, [resultList, term]);

  const isActive = React.useCallback(
    (item: AutocompleteResultItem) => {
      const itemIndex = resultList.findIndex(
        (resultItem) => resultItem.id === item.id,
      );
      return itemIndex === selectedIndex;
    },
    [selectedIndex, resultList],
  );

  const attached = React.useCallback(
    () => !complement && !moreInfoLabel,
    [complement, moreInfoLabel],
  );

  const clear = React.useCallback(() => {
    setSelectedIndex(0);
    setValue('');
    onClearSelection?.();
  }, [setValue, setSelectedIndex]);

  const selectItem = React.useCallback(
    (item?: AutocompleteResultItem) => {
      const resultItem = item ?? resultList?.[selectedIndex];
      onSelectedItem?.(resultItem, clear);
    },
    [resultList, selectedIndex, clear],
  );

  const eventPreparation = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (mode !== 'inline') {
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
      return false;
    },
    [mode],
  );

  const getCurrentItemHeightActive = React.useCallback(
    () =>
      listRef?.current?.querySelector('.active')?.getBoundingClientRect?.()
        ?.height ?? 0,
    [listRef],
  );

  const onEnter = React.useCallback(() => {
    setValue(getResult());
    selectItem();
    setSelectedIndex(0);
  }, [setValue, getResult, selectItem]);

  const onEscape = React.useCallback(() => clear(), [clear]);

  const onArrowUp = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (eventPreparation(event)) {
        const min = 0;
        setSelectedIndex((actualSelectedIndex) =>
          --actualSelectedIndex < min ? min : actualSelectedIndex,
        );
        listRef?.current?.scrollBy({
          behavior: 'smooth',
          top: getCurrentItemHeightActive() * -1,
        });
      }
    },
    [setSelectedIndex],
  );

  const onArrowDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (eventPreparation(event)) {
        const max = resultList.length - 1;
        setSelectedIndex((actualSelectedIndex) =>
          ++actualSelectedIndex >= max ? max : actualSelectedIndex,
        );
        listRef?.current?.scrollBy({
          behavior: 'smooth',
          top: getCurrentItemHeightActive(),
        });
      }
    },
    [setSelectedIndex, resultList],
  );

  const onFocus = React.useCallback(() => {
    setIsInputFocused(true);
    setIsOpen(true);
  }, []);

  React.useEffect(() => {
    if (value !== term) {
      setHasError(false);
      setResultList([]);
    }
    if (value === '') {
      onClearSelection?.();
    }
  }, [term, value]);

  React.useEffect(() => {
    setHasError(false);
    setResultList([]);
    if (!term || minLengthToSearch > term?.length) {
      return;
    }
    setIsLoading(true);
    const afterSearchTerm = onSearchTerm(term);
    if (Array.isArray(afterSearchTerm)) {
      setResultList(afterSearchTerm);
      return setIsLoading(false);
    }
    afterSearchTerm
      .then(setResultList)
      .catch(() => setHasError(true))
      .finally(() => setIsLoading(false));
  }, [term]);

  React.useEffect(
    () => setHasError(resultList.some((item) => !item?.id || !item?.label)),
    [resultList],
  );

  React.useEffect(
    () => setIsOpen(foundResult && isInputFocused),
    [resultList, foundResult],
  );

  return (
    <AutocompleteWrapper ref={wrapperRef} isOpen={isOpen} mode={mode}>
      <BaSeInput
        id={id}
        name={name}
        dataset={dataset}
        width={width}
        showHelpButton={showHelpButton}
        complement={complement}
        moreInfoLabel={moreInfoLabel}
        moreInfoDetails={moreInfoDetails}
        size={size}
        subLabel={subLabel}
        helpButtonProps={helpButtonProps}
        label={label}
        inputMode={inputMode}
        placeholder={placeholder}
        value={value}
        isDisabled={isDisabled}
        isLoading={isLoading}
        iconButton={{
          action: !!value?.length ? clear : undefined,
          color: BaSeTheme.colors.institucionais.azulSebrae,
          name: !!value?.length ? 'close' : 'search',
          typeButton: 'base-icon',
        }}
        onSpecificKeyDown={[
          { listenerKey: 'Enter', callback: onEnter },
          { listenerKey: 'Escape', callback: onEscape },
          { listenerKey: 'ArrowUp', callback: onArrowUp },
          { listenerKey: 'ArrowDown', callback: onArrowDown },
        ]}
        inputStatus={hasError ? 'invalid' : 'normal'}
        onChange={(newValue) => setValue(newValue)}
        onFocus={onFocus}
      />
      {foundResult &&
        (mode === 'list' ? (
          <>
            {isOpen && attached() && <HideTopOfAutocompleteResultList />}
            <AutocompleteResultList
              ref={listRef}
              attached={attached()}
              hasError={hasError}
              isOpen={isOpen}
            >
              {resultList.map((item) => (
                <AutocompleteResultListItem
                  hasStatus={!!item.status}
                  className={`BaSe--autocomplete-result-list-item ${
                    isActive(item) ? 'active' : ''
                  }`}
                  key={item.id}
                  onClick={() => {
                    setValue(item.label);
                    selectItem(item);
                    setIsInputFocused(false);
                    setIsOpen(false);
                  }}
                >
                  {item?.imageSource && (
                    <AutocompleteResultItemImage
                      size={imageAndIconSize}
                      src={item.imageSource}
                      alt={`Imagem de ${item?.label}`}
                    />
                  )}
                  {item?.iconName && (
                    <BaSeIcon
                      name={item.iconName}
                      size={imageAndIconSize}
                      description={`Ícone do item: ${item?.label}`}
                      color={
                        isActive(item)
                          ? BaSeTheme.colors.defaultColors.white
                          : BaSeTheme.colors.defaultColors.black
                      }
                    />
                  )}
                  <>
                    <BaSeParagraph
                      color={
                        isActive(item)
                          ? BaSeTheme.colors.defaultColors.white
                          : BaSeTheme.colors.defaultColors.black
                      }
                    >
                      {item?.label}
                    </BaSeParagraph>
                    {item?.description && (
                      <>
                        <br />
                        <BaSeSmall1
                          color={
                            isActive(item)
                              ? BaSeTheme.colors.institucionais.cinzaSebrae75
                              : BaSeTheme.colors.institucionais.cinzaSebrae45
                          }
                        >
                          {item?.description}
                        </BaSeSmall1>
                      </>
                    )}
                    {item?.status && (
                      <>
                        <br />
                        <BaSeSmall1
                          isBold={true}
                          color={
                            isActive(item)
                              ? BaSeTheme.colors.defaultColors.white
                              : item?.statusColor
                                ? item?.statusColor
                                : BaSeTheme.colors.institucionais.cinzaSebrae45
                          }
                        >
                          {item?.status}
                        </BaSeSmall1>
                      </>
                    )}
                  </>
                </AutocompleteResultListItem>
              ))}
              {extraActions.map((action, index) => (
                <ExtraAction
                  extraAction={action}
                  key={index}
                  isEmpty={!resultList?.length}
                  term={term}
                  setIsOpen={setIsOpen}
                />
              ))}
              {extraItems.map((action, index) => (
                <ExtraItem
                  extraItem={action}
                  key={index}
                  isEmpty={!resultList?.length}
                />
              ))}
            </AutocompleteResultList>
          </>
        ) : (
          <AutocompleteResultInline
            size={size}
            dangerouslySetInnerHTML={{ __html: resultInline() }}
          />
        ))}
    </AutocompleteWrapper>
  );
};

BaSeAutocomplete.displayName = 'BaSeAutocomplete';
